import { useEffect, useMemo, useState } from 'react';
import excelIcon from '@/assets/excel-icon.png';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { RFIItem, RFIStatus, RFIPriority } from '@/types/rfiItem';
import { useRFI } from '@/context/RFIContext';
import { ProjectHeader } from '@/components/ProjectHeader';
import { downloadRfiExcel, listRfiRequests } from '@/lib/api/rfi';
import { getProjectWithRfi } from '@/lib/api/projects';
import { useProjectRole } from '@/hooks/useProjectRole';
import { BackendError } from '@/lib/backend';
import type { RFIRequestListItem } from '@/types/backend';
import { RFIGrid } from '@/components/rfi/RFIGrid';
import type { RfiSaveState } from '@/components/rfi/useRfiAutosave';
import { updateRfiReturnPath } from './rfiNavigation';
import { RevisedRfiHeader } from '@/components/rfi/RevisedRfiHeader';

const statusConfig: Record<RFIStatus, { label: string; dot: string }> = {
  'Open': { label: 'Open', dot: 'bg-foreground/40' },
  'In Progress': { label: 'In Progress', dot: 'bg-foreground/60' },
  'Received': { label: 'Received', dot: 'bg-foreground' },
  'Provided': { label: 'Provided', dot: 'bg-foreground/80' },
  'Closed': { label: 'Closed', dot: 'bg-foreground' },
};

const priorityConfig: Record<RFIPriority, { label: string }> = {
  'Critical': { label: 'Critical' },
  'High': { label: 'High' },
  'Medium': { label: 'Medium' },
  'Low': { label: 'Low' },
};

function mapBackendPriority(p: RFIRequestListItem["priority"]): RFIPriority {
  switch (p) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
  }
}

function mapBackendStatus(s: RFIRequestListItem["status"]): RFIStatus {
  switch (s) {
    case "open":
      return "Open";
    case "in_progress":
      return "In Progress";
    case "closed":
      return "Received";
  }
}

function mapBackendCategory(t: RFIRequestListItem["type"]): string {
  switch (t) {
    case "business_context":
      return "Business context";
    case "historical_trading":
      return "Historical trading";
    case "historical_balance_sheet_and_cash_flows":
      return "Historical balance sheet and cash flows";
    case "forecast_trading":
      return "Forecast trading";
  }
}

function formatBackendDate(date: string | null | undefined): string {
  if (!date) return "";

  // Prefer stable editable format for round-tripping (YYYY-MM-DD).
  if (/^\d{4}-\d{2}-\d{2}/.test(date)) return date.slice(0, 10);

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toISOString().slice(0, 10);
}

function mapBackendItemToUi(item: RFIRequestListItem): RFIItem {
  return {
    id: String(item.id),
    ref: item.ref,
    priority: mapBackendPriority(item.priority),
    status: mapBackendStatus(item.status),
    date: formatBackendDate(item.date),
    area: item.area,
    documentRef: item.document_ref,
    request: item.requests,
    managementComment: item.management_comment ?? "",
    advisorNotes: item.advisor_notes ?? "",
    category: mapBackendCategory(item.type),
  };
}

const RFITracker = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const isUploadMode = searchParams.get("mode") === "upload";
  const isRevisedRfiMode = window.location.pathname === "/revised-rfi";
  const { canEdit } = useProjectRole(projectId);

  // Fetch project data for header
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectId ? getProjectWithRfi(projectId) : Promise.reject('No project ID'),
    enabled: !!projectId,
  });

  const { rfiItems: contextItems, uploadedDocuments, updateRFIStatus, updateRFIComment } = useRFI();
  const [items, setItems] = useState<RFIItem[]>(isUploadMode ? [] : contextItems);
  const [itemsSource, setItemsSource] = useState<"context" | "backend">("context");
  const [rfiSaveState, setRfiSaveState] = useState<RfiSaveState | null>(null);
  
  const [filterStatus, setFilterStatus] = useState<RFIStatus | 'All'>('All');
  const [filterPriority, setFilterPriority] = useState<RFIPriority | 'All'>('All');
  const [filterCategory, setFilterCategory] = useState<string>('All');
  

  useEffect(() => {
    if (isUploadMode) {
      setItems([]);
      return;
    }
    if (itemsSource === "backend") return;
    setItems(contextItems);
  }, [contextItems, itemsSource, isUploadMode]);

  useEffect(() => {
    if (!projectId || isUploadMode) return;

    let cancelled = false;
    (async () => {
      try {
        const rows = await listRfiRequests(projectId);
        if (cancelled) return;
        setItems(rows.map(mapBackendItemToUi));
        setItemsSource("backend");
      } catch (e) {
        if (cancelled) return;
        const message =
          e instanceof BackendError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : "Unknown error";
        toast({
          title: "Failed to load RFI requests",
          description: message,
          variant: "destructive",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, isUploadMode]);

  const categories = useMemo(() => {
    const cats = new Set(items.map(item => item.category));
    return ['All', ...Array.from(cats)];
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (filterStatus !== 'All' && item.status !== filterStatus) return false;
      if (filterPriority !== 'All' && item.priority !== filterPriority) return false;
      if (filterCategory !== 'All' && item.category !== filterCategory) return false;
      return true;
    });
  }, [items, filterStatus, filterPriority, filterCategory]);

  const stats = useMemo(() => {
    const total = items.length;
    const received = items.filter(i => i.status === 'Received').length;
    const inProgress = items.filter(i => i.status === 'In Progress').length;
    const open = items.filter(i => i.status === 'Open').length;
    const outstanding = open + inProgress;
    const progress = total > 0 ? (received / total) * 100 : 0;
    return { total, received, inProgress, open, outstanding, progress };
  }, [items]);

  const groupedItems = useMemo(() => {
    const groups: Record<string, typeof filteredItems> = {};
    filteredItems.forEach(item => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    return groups;
  }, [filteredItems]);

  const handleStatusChange = (id: string, status: RFIStatus) => {
    if (itemsSource === "backend") {
      setItems(prev => prev.map(item => (item.id === id ? { ...item, status } : item)));
    } else {
      updateRFIStatus(id, status);
    }
    toast({ title: 'Status updated' });
  };

  const handleCommentChange = (id: string, comment: string) => {
    if (itemsSource === "backend") {
      setItems(prev => prev.map(item => (item.id === id ? { ...item, managementComment: comment } : item)));
    } else {
      updateRFIComment(id, comment);
    }
  };

  const handleAdvisorNotesChange = (id: string, notes: string) => {
    // пока нет PATCH эндпоинта — держим локально в UI (и для backend, и для context)
    setItems(prev => prev.map(item => (item.id === id ? { ...item, advisorNotes: notes } : item)));
  };

  const handleExport = async () => {
    if (!projectId) {
      toast({
        title: "No active project selected",
        description: "Please open the tracker from a project.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (rfiSaveState && !(await rfiSaveState.handleSave())) return;
      await downloadRfiExcel(projectId);
    } catch (e) {
      const message =
        e instanceof BackendError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : "Unknown error";
      toast({
        title: "Export failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleReturnToUpdateRfi = async () => {
    if (!projectId) return;
    if (rfiSaveState && !(await rfiSaveState.handleSave())) return;
    navigate(updateRfiReturnPath(projectId));
  };

  const rfiActions = (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {rfiSaveState && canEdit && rfiSaveState.status !== "idle" && (
        <div className="mr-2 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span role="status" aria-live="polite">
            {rfiSaveState.status === "error" ? "Not saved" : rfiSaveState.hasUnsavedChanges ? "Saving…" : "Saved"}
          </span>
          {rfiSaveState.status === "error" && (
            <button type="button" className="text-destructive underline" onClick={() => void rfiSaveState.handleSave()}>
              Retry
            </button>
          )}
        </div>
      )}
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 h-8 px-2.5 text-[11px] leading-[1.5] [&_svg]:size-3.5"
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.xlsx,.xls,.csv';
          input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
              toast({ title: 'RFI uploaded', description: `${file.name} has been uploaded successfully.` });
            }
          };
          input.click();
        }}
      >
        <img src={excelIcon} alt="Excel" className="w-4 h-4" />
        Upload RFI
      </Button>

      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 h-8 px-2.5 text-[11px] leading-[1.5] [&_svg]:size-3.5"
        onClick={handleExport}
        disabled={!projectId}
      >
        <img src={excelIcon} alt="Excel" className="w-4 h-4" />
        Download
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <ProjectHeader
        title={projectData?.name}
        beforeNavigate={rfiSaveState?.handleSave}
        backTo={isRevisedRfiMode ? undefined : projectId ? `/databook-builder?projectId=${encodeURIComponent(projectId)}` : "/databook-builder"}
      />

      <main className="container mx-auto px-8 py-8">
        {isRevisedRfiMode && (
          <RevisedRfiHeader
            onBackToUpdateRfi={projectId
              ? () => void handleReturnToUpdateRfi()
              : undefined}
          />
        )}


        {!isRevisedRfiMode && (
          <>
            {/* Stats Summary - 4 separate cards */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              <Card className="border-border">
                <CardContent className="flex h-9 items-center justify-between gap-2.5 px-3 py-0">
                  <span className="text-xl leading-[1.3] font-semibold text-foreground">{stats.total}</span>
                  <p className="text-[11px] leading-[1.5] text-muted-foreground">Total Items</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="flex h-9 items-center justify-between gap-2.5 px-3 py-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xl leading-[1.3] font-semibold text-foreground">{stats.open}</span>
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50" />
                  </div>
                  <p className="text-[11px] leading-[1.5] text-muted-foreground">Open</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="flex h-9 items-center justify-between gap-2.5 px-3 py-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xl leading-[1.3] font-semibold text-foreground">{stats.inProgress}</span>
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/70" />
                  </div>
                  <p className="text-[11px] leading-[1.5] text-muted-foreground">In Progress</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="flex h-9 items-center justify-between gap-2.5 px-3 py-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xl leading-[1.3] font-semibold text-foreground">{stats.received}</span>
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground" />
                  </div>
                  <p className="text-[11px] leading-[1.5] text-muted-foreground">Received</p>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-end gap-3 mb-5">
              {rfiActions}
            </div>

          </>
        )}

        {/* RFI Grid (same component/style as /rfi) */}
        <RFIGrid
          key={`${projectId ?? "none"}:${isUploadMode}`}
          showStatusToolbar={isRevisedRfiMode}
          toolbarActions={isRevisedRfiMode ? rfiActions : undefined}
          projectId={isUploadMode ? undefined : projectId ?? undefined}
          filterStatus={filterStatus}
          filterPriority={filterPriority}
          filterCategory={filterCategory}
          onExposeSave={setRfiSaveState}
          canEdit={canEdit}
        />

        {/* Filters above affect the grid rows shown; saving still syncs the full underlying list safely. */}
      </main>
    </div>
  );
};

export default RFITracker;
